/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  ConnectDragPreview,
  ConnectDragSource,
  DragElementWrapper,
  DragSource,
  DropTarget,
} from 'react-dnd';
import { first, isEmpty, map, size, take } from 'es-toolkit/compat';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { oc } from 'ts-optchain';
import Hammer from 'hammerjs';
import clsx from 'clsx';

import * as Service from 'types/service';
import { DndItemTypes } from 'constants/dndItemTypes';
import {
  EventAction,
  EventAttributesActionLocation,
} from 'utils/analytics/amplitudeEventConstants';
import { GRID_SIZE, getDefaultLocation } from 'constants/AerialConstants';
import {
  NUM_PRE_WITHOUT_SEATED,
  NUM_PRE_WITH_SEATED,
} from 'constants/service/preassignmentConstants';
import { PartyDragItem } from 'components/service/party/useDraggableParty';
import { PrintChitSource } from 'constants/PrinterConstants';
import { ServicePartyDragMode } from 'state/service/ui';
import { ShowAvailabilityOptions } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { formatReservationTime } from 'utils/timeUtils';
import {
  getExpectedReservationLocalDateTime,
  hasPreassignmentConflict,
  serviceTableDropTargetFunctions,
} from 'utils/service/tableUtils';
import { isTouchDevice } from 'utils/touchUtils';
import { partySource } from 'components/service/party/PartyDrag';
import AerialTable, { Props as AerialTableProps } from 'components/aerial/AerialTable';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';
import ServiceAerialTablePopover from './ServiceAerialTablePopover';
import ServiceAerialTableSeatedTime from 'components/service/aerial/ServiceAerialTableSeatedTime';
import ServiceTablePreassignment from 'components/service/service-table/ServiceTablePreassignment';
import TableStates from 'constants/service/tableStateConstants';

const serviceAerialTableSource = {
  beginDrag(props: ExternalProps) {
    return {
      location: props.location || getDefaultLocation(), // Put the default location in so that we can drag even a table w/o a location
      backgroundClassName: props.backgroundClassName,
      foregroundClassName: props.foregroundClassName,
      serverColorClassName: props.serverColorClassName,
      sizeOverride: props.sizeOverride,
      id: props.id,
      name: props.name,
      size: props.size,
    };
  },

  canDrag(props: ExternalProps): boolean {
    return !props.isAssigningSomething && props.canDragTables;
  },
};

interface DropConnectProps {
  canDrop: boolean;
  connectDropTarget: DragElementWrapper<any>;
  isOver: boolean;
  dropItem: PartyDragItem;
}

interface DragConnectProps {
  isDragging: boolean;
  connectDragPreview: ConnectDragPreview;
  connectDragSource: ConnectDragSource;
}

export interface PartyDragConnectProps {
  isDraggingParty: boolean;
  connectPartyDragPreview: ConnectDragPreview;
  connectPartyDragSource: ConnectDragSource;
}

interface State {
  isPopoverOpen: boolean;
}

interface ExternalProps extends AerialTableProps {
  canDragTables: boolean;
  dragMode: ServicePartyDragMode;
  isReadOnly: boolean;
  isSelectedParty: boolean;
  isAnySelection: boolean;
  isSelectedTable: boolean;
  isAssigningSomething: boolean;
  location?: TableLocation;
  onMoveParty(partyId: number, tableId: number, disableAutoSeat: boolean): void;
  onShowAvailabilityPanel(options?: ShowAvailabilityOptions): void;
  onSwapParties(
    droppedParty: Service.Party,
    originalParty: Service.Party,
    fromTableId: number,
    toTableId: number
  ): void;
  onPrintChit(party: Service.Party | undefined, source: PrintChitSource): void;
  onSelectParty(party: Service.Party): void;
  onTouchStart(): void;
  onClosePartyPopover(): void;
  party?: Service.Party;
  partySeatedTimeSec?: number;
  selectedPrimaryState?: string;
  showingPreassignments: boolean;
  showingAerialTableTimer: boolean;
  sortedPreassignedParties?: Service.Party[];
  state: string;
  showContextMenu: boolean;
  noExtraOffset?: boolean;
  timeZone: string;
}

type Props = ExternalProps & DropConnectProps & DragConnectProps & PartyDragConnectProps;

export class ServiceAerialTable extends PureBaseComponent<Props, State> {
  tableNode: React.RefObject<HTMLDivElement> = React.createRef();
  mc!: HammerManager;

  static defaultProps = {
    location: getDefaultLocation(),
  };

  state = {
    isPopoverOpen: false,
  };

  componentDidMount(): void {
    if (this.props.connectDragPreview) {
      // Use empty image as a drag preview so browsers don't draw it
      // and we can draw whatever we want on the custom drag layer instead.
      this.props.connectDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
      });
    }
    if (this.props.connectPartyDragPreview) {
      // Use empty image as a drag preview so browsers don't draw it
      // and we can draw whatever we want on the custom drag layer instead.
      this.props.connectPartyDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
      });
    }

    if (isTouchDevice()) {
      const { current: refElement } = this.tableNode;

      if (refElement) {
        this.mc = new Hammer.Manager(refElement, { touchAction: 'pan-y' });
        this.mc.add(
          new Hammer.Press({
            event: 'longpress',
            time: 500,
          })
        );
        this.mc.on('longpress', this.togglePopover);
      }
    }
  }

  componentWillUnmount() {
    if (this.mc) {
      this.mc.off('longpress', this.togglePopover);
    }
  }

  getStyle(): { left: string; top: string } {
    if (this.props.isDragPreview) {
      return {
        left: `${oc(document.documentElement).scrollLeft()}px`,
        top: `${oc(document.documentElement).scrollTop()}px`,
      };
    }
    const location = this.props.location ?? getDefaultLocation();
    return {
      left: `${GRID_SIZE * location.xPosition}px`,
      top: `${GRID_SIZE * location.yPosition}px`,
    };
  }

  handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    this.togglePopover();
  }

  togglePopover() {
    if (!this.props.showContextMenu) {
      return;
    }
    if (!this.state.isPopoverOpen) {
      amplitudeSendEvent(EventAction.PARTY_STATUS_QUICK_ACTION_OPENED, {
        actionLocation: EventAttributesActionLocation.TABLE_CONTEXT_MENU,
      });
    }
    this.setState({
      isPopoverOpen: !this.state.isPopoverOpen,
    });
  }

  render() {
    const location = this.props.location ?? getDefaultLocation();
    const {
      connectPartyDragSource: connectPartyDragSourceFromProps,
      canDrop,
      isOver,
      dropItem,
      isAnySelection,
      isSelectedParty,
      isSelectedTable,
      partySeatedTimeSec,
      party,
      selectedPrimaryState,
      sortedPreassignedParties,
      showingPreassignments,
      showingAerialTableTimer,
      showContextMenu,
      isReadOnly,
      timeZone,
    } = this.props;
    const tableState = this.props.state;
    const numPreassignmentsToRender = partySeatedTimeSec
      ? NUM_PRE_WITH_SEATED
      : NUM_PRE_WITHOUT_SEATED;
    const preassignmentsToRender: Service.Party[] = take(
      sortedPreassignedParties,
      numPreassignmentsToRender
    );
    const noopConnector = (node: any) => node;
    const connectDragSource = this.props.connectDragSource || noopConnector;
    const connectPartyDragSource =
      showContextMenu && !this.state.isPopoverOpen && !isReadOnly
        ? connectPartyDragSourceFromProps
        : noopConnector;
    const connectDropTarget = this.props.connectDropTarget || noopConnector;
    const isDropTarget = isOver && canDrop;
    const minPartySize = this.props.minPartySize || this.props.size;
    const isDropTargetGood =
      dropItem &&
      dropItem.mode === 'SERVICE' &&
      oc(dropItem).party.expectedPartySize(0) >= minPartySize &&
      oc(dropItem).party.expectedPartySize(0) <= this.props.size;

    const tableIds = isDropTargetGood ? dropItem.party.tableIds : [];

    const isNotReady = tableState !== TableStates.ready;

    const isPreassignmentSelected = selectedPrimaryState === 'EXPECTED';

    const isUnselectable = isPreassignmentSelected
      ? false
      : !isSelectedParty &&
        !isSelectedTable &&
        isAnySelection &&
        this.props.partySeatedTimeSec &&
        this.props.isAssigningSomething;

    const className = clsx(
      'ServiceAerialTable',
      `ServiceAerialTable--visualSize${location.visualSize}`,
      {
        'is-selected': isSelectedTable,
        'has-seated-party': !!this.props.partySeatedTimeSec,
        'has-assigned-party': !isEmpty(this.props.sortedPreassignedParties),
        'is-unassignable': isUnselectable,
        'is-unassigned':
          !isSelectedParty &&
          !isSelectedTable &&
          isAnySelection &&
          !this.props.isAssigningSomething,
        'is-drop-target': isDropTarget,
        'is-drop-target-good': isDropTargetGood && showContextMenu,
        'is-drop-target-invalid': dropItem && !isDropTargetGood && showContextMenu,
        'is-dragging': this.props.isDraggingParty,
        'is-popover-open':
          this.state.isPopoverOpen ||
          (dropItem && tableIds.includes(this.props.id) && showContextMenu),
      }
    );

    const aerialTableModifierClassName = clsx('ServiceAerialTableInternal', {
      'is-drop-target': isDropTarget,
      'is-unassignable': isNotReady,
    });

    const notReadyIndicatorClassName = clsx('ServiceAerialTable-notReadyIndicator', {
      'is-blocked': tableState === TableStates.blocked,
      'is-dirty': tableState === TableStates.dirty,
    });

    return connectDropTarget(
      connectPartyDragSource(
        connectDragSource(
          <div className={className} style={this.getStyle()} data-testid="service-table">
            <div
              ref={this.tableNode}
              onContextMenu={isTouchDevice() ? undefined : this.handleContextMenu}
              onTouchStart={this.props.onTouchStart}
            >
              <AerialTable
                {...this.props}
                modifierClassName={aerialTableModifierClassName}
                touch-action="auto"
              />
            </div>

            <div className="ServiceAerialTable-partyDetails">
              {showingAerialTableTimer && partySeatedTimeSec && (
                <ServiceAerialTableSeatedTime seatedTimeSec={partySeatedTimeSec} />
              )}
              {showingPreassignments &&
                sortedPreassignedParties &&
                map(preassignmentsToRender, (preassignment, idx) => {
                  const preassignmentTime = getExpectedReservationLocalDateTime(
                    preassignment,
                    timeZone
                  );
                  return (
                    <ServiceTablePreassignment
                      className="ServiceAerialTable-preassignment ServiceAerialTable-timeBubble"
                      displayTimeAsShort={true}
                      key={preassignment.id}
                      isPinned={oc(preassignment).visit.pinnedToTable() || undefined}
                      isOverlapping={hasPreassignmentConflict(
                        preassignment,
                        idx,
                        sortedPreassignedParties,
                        timeZone,
                        party
                      )}
                      preassignmentTime={
                        preassignmentTime && formatReservationTime(preassignmentTime)
                      }
                    />
                  );
                })}

              {showingPreassignments &&
                size(sortedPreassignedParties) > numPreassignmentsToRender && (
                  <div className="ServiceAerialTable-timeBubble ServiceAerialTable-hasMorePreassignments">
                    …
                  </div>
                )}
            </div>
            {isNotReady && <div className={notReadyIndicatorClassName} />}
            {this.state.isPopoverOpen && (
              <ServiceAerialTablePopover
                isOpen={this.state.isPopoverOpen}
                onClose={this.togglePopover}
                party={this.props.party}
                nextParty={first(this.props.sortedPreassignedParties)}
                tableId={this.props.id}
                tableState={this.props.state}
                onPrintChit={this.props.onPrintChit}
                onShowAvailabilityPanel={this.props.onShowAvailabilityPanel}
                onSelectParty={this.props.onSelectParty}
                onTableClicked={this.props.onTableClicked}
                tableNode={this.tableNode}
                isReadOnly={isReadOnly}
              />
            )}
          </div>
        )
      )
    );
  }
}

const { dropTarget: serviceAerialTableTarget, collectFunction: collectTarget } =
  serviceTableDropTargetFunctions;

const dragSource = DragSource<
  ExternalProps & DropConnectProps & PartyDragConnectProps,
  DragConnectProps
>(DndItemTypes.SERVICE_AERIAL_TABLE, serviceAerialTableSource, (connect, monitor) => {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  };
})(ServiceAerialTable);

interface PartyDragProps {
  id: number;
  canDragTables: boolean;
  party?: Service.Party;
  sortedPreassignedParties?: Service.Party[];
  isAssigningSomething: boolean;
  isReadOnly: boolean;
  dragMode: ServicePartyDragMode;
  noExtraOffset?: boolean;
}

export function partyTableSource<T extends PartyDragProps>() {
  return {
    beginDrag(props: T) {
      const party = props.party || first(props.sortedPreassignedParties);
      return partySource.beginDrag({
        dragSource: 'TABLE',
        fromTableId: props.id,
        party,
        noExtraOffset: props.noExtraOffset,
      });
    },
    canDrag(props: T) {
      const party = props.party || first(props.sortedPreassignedParties);
      const { canDragTables, isReadOnly, dragMode, isAssigningSomething } = props;
      return (
        partySource.canDrag({
          canDragTables,
          isReadOnly,
          party,
          dragMode,
        }) && !isAssigningSomething
      );
    },
  };
}

const partyDragSource = DragSource<ExternalProps & DropConnectProps, PartyDragConnectProps>(
  DndItemTypes.PARTY,
  partyTableSource<ExternalProps>(),
  (connect, monitor) => {
    return {
      connectPartyDragSource: connect.dragSource(),
      connectPartyDragPreview: connect.dragPreview(),
      isDraggingParty: monitor.isDragging(),
    };
  }
)(dragSource);

export default DropTarget<ExternalProps, DropConnectProps, PartyDragItem>(
  DndItemTypes.PARTY,
  serviceAerialTableTarget,
  collectTarget
)(partyDragSource);
